import { useState } from "react";
import { Sparkles, ArrowRight, RotateCcw } from "lucide-react";
import { activateCycle, getLifecycle } from "@/api";
import { newCycleInitialForm, initialFocusForm, focusFromForm, missingFocusFields, runActivation } from "@/lib/cycleFlow";

const LABELS = {
  brand: "Your Name / Brand",
  searchableTitle: "Searchable Title",
  audience: "Audience / Who You Help",
  niche: "Niche / Focus",
  audienceShort: "Who You Help — Short (optional)",
  bio: "AEO-Ready Bio",
};
const TEXT_FIELDS = ["brand", "searchableTitle", "audience", "niche"];
const inputStyle = {
  width: "100%", marginTop: 6, padding: "0.7rem 0.85rem", borderRadius: 10,
  background: "#16181d", color: "#f4f4f5", border: "1px solid #2a2e37", fontSize: "0.95rem",
};

// Paid-entry surface. The activation focus must be explicitly reviewed/confirmed by the customer.
// A NEW cycle starts with FRESH focus-specific fields (Creative Work/Niche, Who You Help, Who You
// Help Short, Searchable Title, AEO-ready Bio); only stable identity (Name / Brand) is preserved
// from the account profile. The prior cycle's focus is never silently reused — the customer may
// intentionally restore it with "Use my last setup". Activation happens ONLY on the explicit
// confirm-and-start click (never on render).
export default function FindabilityCycleReady({ count = 1, user, onActivated }) {
  const profile = (user && user.profile) || {};
  const [form, setForm] = useState(() => newCycleInitialForm(profile));
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);

  const focus = focusFromForm(form);
  const missing = missingFocusFields(focus);
  const canStart = missing.length === 0 && !busy;

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Intentional, customer-initiated restore of the prior setup values. Never runs automatically.
  const useLastSetup = () => setForm(initialFocusForm(profile));

  const start = async () => {
    if (!canStart) return;
    setBusy(true);
    setConflict(null);
    const res = await runActivation({ focus: focusFromForm(form), activateCycle, getLifecycle });
    setBusy(false);
    if (res.ok) onActivated(res.lifecycle);
    else setConflict(res.conflict);
  };

  return (
    <div
      data-testid="cycle-ready-card"
      style={{ maxWidth: 560, margin: "8vh auto", padding: "2.5rem", background: "#0f1115",
               color: "#f4f4f5", borderRadius: 20, border: "1px solid #23262d",
               boxShadow: "0 24px 60px rgba(0,0,0,.35)" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#8b5cf6",
                    fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", fontSize: 12 }}>
        <Sparkles size={16} /> Findability Cycle
      </div>
      <h1 data-testid="cycle-ready-title" style={{ fontSize: "2rem", lineHeight: 1.15, margin: ".6rem 0 .4rem" }}>
        Your Findability Cycle is ready
      </h1>
      <p data-testid="cycle-ready-count" style={{ color: "#a1a1aa", margin: 0 }}>
        You have {count} available Findability Cycle{count === 1 ? "" : "s"}.
      </p>
      <p style={{ color: "#a1a1aa", marginTop: 14, fontSize: ".95rem" }}>
        Define the focus for this cycle. Your Name / Brand carries over — the rest starts fresh so
        you can set a new theme. Prefer your previous setup? Use the button below.
      </p>

      <button
        type="button"
        data-testid="use-last-setup-btn"
        onClick={useLastSetup}
        disabled={busy}
        style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 8,
                 padding: "0.5rem 0.9rem", borderRadius: 999, cursor: busy ? "not-allowed" : "pointer",
                 background: "transparent", color: "#a78bfa", border: "1px solid #3a3550",
                 fontWeight: 600, fontSize: ".85rem" }}
      >
        <RotateCcw size={14} /> Use my last setup
      </button>

      <div data-testid="focus-confirm-form" style={{ marginTop: "1.2rem", display: "grid", gap: "0.9rem" }}>
        {TEXT_FIELDS.map((k) => (
          <label key={k} style={{ fontSize: ".8rem", color: "#c7c7cf", fontWeight: 600 }}>
            {LABELS[k]}
            <input
              data-testid={`focus-input-${k}`}
              value={form[k]}
              onChange={setField(k)}
              placeholder={LABELS[k]}
              style={inputStyle}
            />
          </label>
        ))}
        <label style={{ fontSize: ".8rem", color: "#c7c7cf", fontWeight: 600 }}>
          {LABELS.audienceShort}
          <input
            data-testid="focus-input-audienceShort"
            value={form.audienceShort}
            onChange={setField("audienceShort")}
            placeholder="A short version of who you help (e.g. first-time authors)."
            style={inputStyle}
          />
        </label>
        <label style={{ fontSize: ".8rem", color: "#c7c7cf", fontWeight: 600 }}>
          {LABELS.bio}
          <textarea
            data-testid="focus-input-bio"
            value={form.bio}
            onChange={setField("bio")}
            placeholder="A short, factual bio the assistant can draw on when drafting your posts."
            rows={4}
            style={{ ...inputStyle, resize: "vertical", lineHeight: 1.4 }}
          />
        </label>
      </div>

      {missing.length > 0 && (
        <div data-testid="cycle-focus-incomplete"
             style={{ marginTop: "1rem", padding: "0.85rem 1rem", borderRadius: 12,
                      background: "#1a1410", border: "1px solid #4a2f17", color: "#f5c078" }}>
          Fill in your {missing.map((m) => LABELS[m] || m).join(", ")} to start your cycle.
        </div>
      )}

      <button
        data-testid="start-findability-cycle-btn"
        onClick={start}
        disabled={!canStart}
        style={{ marginTop: "1.4rem", display: "inline-flex", alignItems: "center", gap: 10,
                 padding: "0.9rem 1.4rem", borderRadius: 999, border: "none",
                 cursor: canStart ? "pointer" : "not-allowed",
                 background: canStart ? "#8b5cf6" : "#3a3550", color: "#fff", fontWeight: 600, fontSize: "1rem" }}
      >
        {busy ? "Starting…" : "Confirm Focus & Start Findability Cycle"} <ArrowRight size={18} />
      </button>

      {conflict && (
        <div data-testid="cycle-activation-conflict"
             style={{ marginTop: "1.2rem", padding: "0.85rem 1rem", borderRadius: 12,
                      background: "#1c1417", border: "1px solid #4a1f27", color: "#f4a3b0" }}>
          {conflict.message}
        </div>
      )}
    </div>
  );
}
